import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { from } from "rxjs";
import { catchError, exhaustMap, map, mergeMap, tap } from "rxjs/operators";

import * as PropertiesActionTypes from "../../actions/properties/properties.action";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { AppState } from "../../reducers";

import { PropertiesService } from "src/app/shared/services/properties/properties.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";

@Injectable()
export class PropertiesEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private propertiesService: PropertiesService,
    private tableConfigService: TableConfigService,
    private translate: TranslateService
  ) {}

  getPropertiesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertiesActionTypes.getPropertiesList),
      exhaustMap((action) =>
        this.propertiesService
          .getProperties(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              PropertiesActionTypes.loadPropertiesList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                PropertiesActionTypes.loadPropertiesList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Properties.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getExcelPropertiesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertiesActionTypes.getExcelPropertiesList),
      exhaustMap((action) =>
        this.propertiesService
          .getProperties(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              PropertiesActionTypes.loadExcelPropertiesList({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                PropertiesActionTypes.loadPropertiesList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Properties.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getPropertiesUsagesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertiesActionTypes.getPropertiesUsagesList),
      exhaustMap((action) =>
        this.propertiesService.getPropertiesUsage(action.pageReq).pipe(
          map((result) =>
            PropertiesActionTypes.loadPropertiesUsagesList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              PropertiesActionTypes.loadPropertiesList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Properties.loadError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getPropertiesDataTypesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertiesActionTypes.getPropertiesDataTypeList),
      exhaustMap((action) =>
        this.propertiesService.getPropertyDataType(action.pageReq).pipe(
          map((result) =>
            PropertiesActionTypes.loadPropertiesDataTypeList({
              response: result,
            })
          ),
          catchError((error) => {
            const actions = [
              PropertiesActionTypes.loadPropertiesList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Properties.loadError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Properties Table Config
  getPropertiesTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertiesActionTypes.getPropertiesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              PropertiesActionTypes.loadPropertiesTableConfig({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                PropertiesActionTypes.loadPropertiesTableConfig({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Properties Table Config
  savePropertiesTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertiesActionTypes.savePropertiesTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            PropertiesActionTypes.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              PropertiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertiesActionTypes.updateProperty),
      exhaustMap((action) =>
        this.propertiesService.updateProperty(action.property).pipe(
          map((result) => {
            return PropertiesActionTypes.getProperty({
              code: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Properties.propertyUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PropertiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Properties.propetyUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertiesActionTypes.createProperty),
      exhaustMap((action) =>
        this.propertiesService.createProperty(action).pipe(
          mergeMap((result) => [
            PropertiesActionTypes.resetCreation(),
            PropertiesActionTypes.getProperty({
              code: result?.result?.code,
            }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Properties.propertyCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PropertiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Properties.propertyCreatedFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  setDisabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertiesActionTypes.setDisabled),
      exhaustMap((action) =>
        this.propertiesService.deleteProperty(action).pipe(
          map((result) =>
            PropertiesActionTypes.getPropertiesList({
              options: {
                paging: {
                  skip: 0,
                  top: 99999,
                },
                filteringArgs: undefined,
                sortingArgs: undefined,
              },
            })
          ),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Properties.setDisabled",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PropertiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Properties.propertyDeletedFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertiesActionTypes.getProperty),
      exhaustMap((action) =>
        this.propertiesService.getProperty(action.code).pipe(
          map((result) =>
            PropertiesActionTypes.setProperty({ property: result })
          ),
          catchError((error) => {
            const actions = [
              PropertiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Properties.loadErrorProperty",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  setEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PropertiesActionTypes.setEnabled),
      exhaustMap((action) =>
        this.propertiesService.updateProperty(action).pipe(
          map((result) => {
            this.store$.dispatch(
              PropertiesActionTypes.getProperty({ code: result?.result?.code })
            );
            return PropertiesActionTypes.getPropertiesList({
              options: {
                paging: {
                  skip: 0,
                  top: 99999,
                },
                filteringArgs: undefined,
                sortingArgs: undefined,
              },
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Properties.setEnabled",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              PropertiesActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Properties.setEnabledFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
